/** Purchase lines — **S3-03**, ADR-0044. And what none of these hooks will ever return: a figure.
 *
 * Every line comes back with the reasons it has no figure (`assessment.absences`), and the generated
 * types have no field that could carry an emission, a multiplier or a price. That is the server's
 * guarantee (`test_the_assessment_schema_cannot_carry_a_figure`) and it is repeated here as a type:
 * a screen cannot render a number it was never given, so the honest sentence is the only one
 * available.
 *
 * **Every read throws on failure and none defaults to empty.** A dropped request rendered as an
 * empty register would say *"no purchases"* — a claim about the company, produced by a connection —
 * which is the defect `Loaded` exists to prevent.
 *
 * **Writes are never retried by the client.** Accepting a ledger is answered by the server if it
 * is repeated (`already_imported`), but a decision or a vendor link whose outcome is unknown is a
 * thing to *look at*; the screens say so rather than offering a button.
 */

import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type ProcurementLine = components["schemas"]["ProcurementLineOut"];
export type ProcurementPreview = components["schemas"]["ProcurementPreviewOut"];
export type SpendAbsence = components["schemas"]["SpendAbsenceOut"];
export type ProcurementAccepted = components["schemas"]["ProcurementAcceptedOut"];
export type ProcurementDecisions = components["schemas"]["ProcurementDecisionsIn"];

const keys = {
  all: ["procurement"] as const,
  lines: (entityId: string | null) => ["procurement", "lines", entityId ?? "all"] as const,
  preview: (documentId: string, entityId: string) =>
    ["procurement", "preview", documentId, entityId] as const,
};

/** Every purchase line this workspace holds, each with why it has no figure. */
export function useProcurementLines(entityId: string | null) {
  return useQuery({
    queryKey: keys.lines(entityId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/procurement/lines", {
        params: { query: entityId ? { legal_entity_id: entityId } : {} },
      });
      if (error || !data) throw refusalFrom(response, error, "The purchase lines could not be read.");
      return data;
    },
  });
}

/** What a stored ledger would record, and everything wrong with it. Writes nothing.
 *
 * `skipToken` rather than `enabled: false` with a cast, for the reason `useStreamOptions` gives:
 * with no document or no company chosen there is nothing to ask, and the type system should say so.
 */
export function useProcurementPreview(documentId: string | null, entityId: string | null) {
  return useQuery({
    queryKey: keys.preview(documentId ?? "none", entityId ?? "none"),
    retry: false,
    queryFn:
      documentId && entityId
        ? async (): Promise<ProcurementPreview> => {
            const { data, error, response } = await api.GET(
              "/api/v1/procurement/imports/{document_id}/preview",
              {
                params: {
                  path: { document_id: documentId },
                  query: { legal_entity_id: entityId },
                },
              },
            );
            if (error || !data) {
              throw refusalFrom(response, error, "That file could not be read as a ledger.");
            }
            return data;
          }
        : skipToken,
  });
}

/** Record every line of a previewed ledger. All or nothing, and a repeat is answered by the server. */
export function useAcceptProcurement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { documentId: string; entityId: string }) => {
      const { data, error, response } = await api.POST("/api/v1/procurement/imports/{document_id}", {
        params: { path: { document_id: input.documentId } },
        body: { legal_entity_id: input.entityId },
      });
      if (error || !data) throw refusalFrom(response, error, "That ledger could not be recorded.");
      return data;
    },
    // The document moved to verified as well, so the evidence lists are stale too.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

/** Replace a line's answers. Empty means *not stated* — the server's rule, not a default. */
export function useDecideLine(lineId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProcurementDecisions) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/procurement/lines/{line_id}/decisions",
        { params: { path: { line_id: lineId } }, body: input },
      );
      if (error || !data) throw refusalFrom(response, error, "Those answers could not be recorded.");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

/** Link a line to a vendor a person chose, or remove the link (`null`). Never a suggestion. */
export function useLinkVendor(lineId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vendorId: string | null) => {
      const { data, error, response } = await api.PUT("/api/v1/procurement/lines/{line_id}/vendor", {
        params: { path: { line_id: lineId } },
        body: { vendor_id: vendorId },
      });
      if (error || !data) throw refusalFrom(response, error, "That vendor could not be linked.");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}
